import {
    getTotalTrialDays,
    isOrgsOnFTEnabled,
} from '@thoughtspot/feature-service';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import {
    PeopleIncrementalPropertiesEnum,
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import { ToastAlertProps } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import {
    Icon,
    IconColor,
    IconID,
} from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, View } from '@thoughtspot/radiant-react/widgets/layout';
import { SafeHTML } from '@thoughtspot/radiant-react/widgets/safe-html';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import {
    getSubscriptionStatus,
    getSubscriptionType,
    hasAdminPrivileges,
    is1MindBotEnabled,
    PurchaseOption,
    setSourceToPricingPage,
    SubscriptionStatus,
    SubscriptionType,
} from '@thoughtspot/session-service';
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTimeout } from '/@utils/use-timeout';
import { OneMindModal } from '../../1Mind/1Mind-modal.container';
import { InviteSource } from '../../invite-users/invite-users.interface';
import { InviteUsersButtonContainer } from '../../invite-users-v2/invite-users-button/invite-users-button.container';
import styles from './trial-info.module.scss';
import {
    discountApplicable,
    getDaysLeftText,
    getDiscountOfferText,
    TrailInfoDataTestId,
    trialMessageDetails,
} from './trial-info.util';

// When less than 7 days are left, info bar changes to yellow.
const WARNING_THRESHOLD = 7;

enum MIXPANEL_BUTTON_TYPE {
    CONTACT_SALES = 'CONTACT_SALES',
    BUY_NOW = 'BUY_NOW',
}

export interface TrialInfoProps {
    collectEvent: (userAction: UserAction, property: PropertyMap) => void;
    updateMixPanelProperty: (
        key: string,
        value: string | number | boolean,
    ) => void;
    trialDaysLeft: number;
    purchaseOption: PurchaseOption;
    goToTeamsPlanOptions: () => void;
    teamsNotEnabled: boolean;
    isDiscountEnabled: boolean;
    startTime: number;
    endTime: number;
    percentageDiscount: number;
    discountOfferDuration: number;
    goToAdmin: (tabId?: string, shouldOpenInNewTab?: boolean) => void;
    expiryTimeStamp: number;
    timezone: string;
    locale: string;
    isCopilotEnabled?: boolean;
    isSpotterFullExperienceEnabled?: boolean;
}

export const TrialInfo: React.FC<TrialInfoProps> = ({
    collectEvent,
    updateMixPanelProperty,
    trialDaysLeft,
    purchaseOption,
    goToTeamsPlanOptions,
    teamsNotEnabled,
    isDiscountEnabled,
    startTime,
    endTime,
    percentageDiscount,
    discountOfferDuration,
    goToAdmin,
    expiryTimeStamp,
    timezone,
    locale,
    isCopilotEnabled = false,
    isSpotterFullExperienceEnabled = true,
}) => {
    const { t } = useTranslation();
    const isUserNotExpired = trialDaysLeft > 0;
    const lessThanThreshold =
        trialDaysLeft < WARNING_THRESHOLD && isUserNotExpired;
    const plansOptions =
        purchaseOption !== PurchaseOption.BUY_NOW || teamsNotEnabled;
    const isSubscriptionCancelled =
        getSubscriptionStatus() === SubscriptionStatus.CANCELED;
    const isFreeTrialEnding =
        getSubscriptionType() === SubscriptionType.FREE_TRIAL &&
        trialDaysLeft < 3;
    // discount offer banner logic
    const isNewDiscountApplicable =
        isDiscountEnabled &&
        !isSubscriptionCancelled &&
        !plansOptions &&
        discountApplicable(startTime, endTime);

    const [isDiscountApplicable, setIsDiscountApplicable] = useState(
        isNewDiscountApplicable,
    );
    const [bannerText, setBannerText] = useState('');
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [showOneMindModal, setShowOneMindModal] = useState<boolean>(false);

    const current_date = Date.now();
    const expiry_date = new Date(expiryTimeStamp);
    const { remainingHours, time, formatedDate } = trialMessageDetails(
        current_date,
        expiry_date,
        timezone,
        locale,
    );
    const daysLeft = getDaysLeftText(
        trialDaysLeft,
        isSubscriptionCancelled,
        remainingHours,
        time,
        formatedDate,
        t,
    );

    const developerEditionBannerText = _.assign(
        t('dev_edition_banner_text', {
            days: trialDaysLeft,
        }),
    );

    const copilotTrialBannerText = _.assign(
        t('copilot.trial.banner.msg', {
            days: trialDaysLeft,
        }),
    );

    const copilotTrialBannerSecondText = _.assign(
        t('copilot.trial.banner.second.msg', {
            days: trialDaysLeft,
        }),
    );
    const [toastAlert, updateToastAlert] = React.useState<ToastAlertProps>({
        isVisible: false,
        message: '',
    });

    const discountBannerText = getDiscountOfferText(
        endTime,
        percentageDiscount,
        discountOfferDuration,
        t,
    );

    const headerClassName = cx(
        styles.trialInfoHeader,
        styles.greaterThanThreshold,
    );
    const iconColor = IconColor.BLUE;

    const diffInDiscountStartTime = startTime - current_date;
    const diffInDiscountEndTime = endTime - current_date;

    const handleBannerText = () => {
        const isNewDiscountApplicable =
            isDiscountEnabled &&
            !isSubscriptionCancelled &&
            !plansOptions &&
            discountApplicable(startTime, endTime);
        setIsDiscountApplicable(isNewDiscountApplicable);
        const discountBannerText = getDiscountOfferText(
            endTime,
            percentageDiscount,
            discountOfferDuration,
            t,
        );
        const newBannerText = isNewDiscountApplicable
            ? discountBannerText
            : daysLeft;
        setBannerText(newBannerText);
    };

    useEffect(() => {
        handleBannerText();
    }, [startTime, endTime]);

    useEffect(() => {
        handleBannerText();
    }, []);

    useTimeout(
        () => {
            const applyDiscount =
                isDiscountEnabled &&
                !isSubscriptionCancelled &&
                !plansOptions &&
                discountApplicable(startTime, endTime);

            if (applyDiscount) {
                const newBannerText = getDiscountOfferText(
                    endTime,
                    percentageDiscount,
                    discountOfferDuration,
                    t,
                );
                setBannerText(newBannerText);
                setIsDiscountApplicable(true);
            }
        },
        {
            delay: diffInDiscountStartTime,
            shouldCall:
                diffInDiscountStartTime > 0 &&
                isDiscountEnabled &&
                !isSubscriptionCancelled,
        },
    );

    useTimeout(
        () => {
            setBannerText(daysLeft);
            setIsDiscountApplicable(false);
        },
        {
            delay: diffInDiscountEndTime,
            shouldCall: diffInDiscountEndTime > 0,
        },
    );

    const mixPanelInfo = {
        totalDaysInTrial: getTotalTrialDays(),
        daysLeftInTrial: daysLeft,
        bannerType: iconColor,
        buttonType: plansOptions
            ? MIXPANEL_BUTTON_TYPE.CONTACT_SALES
            : MIXPANEL_BUTTON_TYPE.BUY_NOW,
        startTime,
        endTime,
        percentageDiscount,
        discountOfferDuration,
    };
    collectEvent(
        new UserAction(userActionTypeEnum.TRIAL_EXPIRY_MESSAGE_SEEN),
        mixPanelInfo,
    );

    const handleContactSalesButtonClick = () => {
        updateMixPanelProperty(
            PeopleIncrementalPropertiesEnum.totalTrialExpiryBtnCLicks,
            1,
        );
        collectEvent(
            new UserAction(userActionTypeEnum.TRIAL_EXPIRY_MESSAGE_CLICKED),
            mixPanelInfo,
        );
        (window as any).Intercom('show');
    };

    const handleViewPlanOptionsButtonClick = () => {
        updateMixPanelProperty(
            PeopleIncrementalPropertiesEnum.totalTrialExpiryBtnCLicks,
            1,
        );
        if (isSubscriptionCancelled) {
            collectEvent(
                new UserAction(
                    userActionTypeEnum.TEAM_REACTIVATE_SUBSCRIPTION_MESSAGE_CLICKED,
                ),
                {
                    daysLeftUntilPlanCanBeReactivated: daysLeft,
                    bannerType: iconColor,
                },
            );
        } else if (discountApplicable) {
            collectEvent(
                new UserAction(userActionTypeEnum.DISCOUNT_EXPERIMENTATION),
                mixPanelInfo,
            );
        } else {
            collectEvent(
                new UserAction(userActionTypeEnum.TRIAL_EXPIRY_MESSAGE_CLICKED),
                mixPanelInfo,
            );
        }
        setSourceToPricingPage(
            isDiscountEnabled ? 'Discount Banner' : 'Free Trail Banner',
        );
        if (isOrgsOnFTEnabled() && hasAdminPrivileges()) {
            goToAdmin('pricing');
        } else goToTeamsPlanOptions();
    };

    const getButtonDetails = () => {
        if (isFreeTrialEnding) {
            return {
                id: 'upgrade_now',
                text: t('upgrade_cluster'),
                onClick: handleContactSalesButtonClick,
                dataTestId: TrailInfoDataTestId.ContactSales,
            };
        }
        if (isSubscriptionCancelled) {
            return {
                id: 'reactivate',
                text: t('reactivate_subscription'),
                onClick: handleViewPlanOptionsButtonClick,
                dataTestId: TrailInfoDataTestId.Reactivate,
            };
        }

        if (plansOptions) {
            return {
                id: 'upgrade_cluster',
                text: t('contact_sales'),
                onClick: handleContactSalesButtonClick,
                dataTestId: TrailInfoDataTestId.ContactSales,
            };
        }
        return {
            id: 'plan_options',
            text: isDiscountApplicable
                ? t('trial_banner_buy_now')
                : t('plan_options'),
            onClick: handleViewPlanOptionsButtonClick,
            dataTestId: TrailInfoDataTestId.PlanOptions,
        };
    };

    const onInviteTeamButtonClick = () => {
        setIsInviteModalOpen(true);
    };

    const renderTrialBannerButtons = (shouldShowSalesButton: boolean) => {
        const buttonDetails = getButtonDetails();
        return (
            <Horizontal
                className={styles.spotterRightLinks}
                data-testid={TrailInfoDataTestId.BannerButtonContainer}
            >
                <Button
                    type="primary"
                    className={cx(styles.ctaButton)}
                    onClick={onInviteTeamButtonClick}
                    data-testid={TrailInfoDataTestId.InviteTeam}
                    text={t('freetrial.banner.invite.team')}
                    backgroundType="light"
                    size="s"
                />
                {shouldShowSalesButton && (
                    <Button
                        type="primary"
                        className={cx(styles.ctaButton)}
                        id={buttonDetails.id}
                        onClick={buttonDetails.onClick}
                        data-testid={buttonDetails.dataTestId}
                        text={buttonDetails.text}
                        backgroundType="light"
                        size="s"
                    />
                )}
                <Button
                    type="primary"
                    className={styles.ctaButton}
                    data-testid={TrailInfoDataTestId.BookExpertHelp}
                    text={t('edge.convAssist.banner.call')}
                    backgroundType="light"
                    size="s"
                    onClick={() => setShowOneMindModal(true)}
                />
            </Horizontal>
        );
    };

    const getTrialBannerButton = () => {
        if (
            getSubscriptionType() === SubscriptionType.DEVELOPER_EDITION ||
            isCopilotEnabled
        ) {
            return <></>;
        }
        if (isOrgsOnFTEnabled() && !hasAdminPrivileges()) {
            return renderTrialBannerButtons(false);
        }

        return renderTrialBannerButtons(true);
    };

    const getTrialBannerText: any = () => {
        if (isCopilotEnabled) {
            if (isSpotterFullExperienceEnabled) {
                return <SafeHTML html={copilotTrialBannerSecondText} />;
            }
            return <SafeHTML html={copilotTrialBannerText} />;
        }

        if (getSubscriptionType() === SubscriptionType.DEVELOPER_EDITION) {
            return (
                <Typography variant="p" noMargin>
                    {developerEditionBannerText}
                </Typography>
            );
        }
        return (
            <Typography variant="p" noMargin>
                {bannerText}
            </Typography>
        );
    };
    const oneMindBotEnabled = is1MindBotEnabled();

    return (
        <>
            {oneMindBotEnabled && showOneMindModal && (
                <OneMindModal
                    showOneMindModal={showOneMindModal}
                    setShowOneMindModal={setShowOneMindModal}
                    hideBot
                />
            )}
            {isInviteModalOpen && (
                <InviteUsersButtonContainer
                    onEmailAdd={null}
                    updateToastAlert={updateToastAlert}
                    isInviteLinkCall
                    onInviteComplete={() => setIsInviteModalOpen(false)}
                    shouldCollectInviteBtnEvent
                    onInviteBtnClickUserAction={
                        new UserAction(
                            userActionTypeEnum.INVITE_TEAMMATES_FT_BANNER,
                        )
                    }
                    source={InviteSource.FT_BANNER}
                />
            )}
            <View className={headerClassName}>
                <Horizontal
                    vAlignContent="center"
                    className={styles.trialInfoContainer}
                >
                    <View>
                        <Icon
                            id={IconID.EXCLAMATION_POINT_CIRCLE}
                            color={iconColor}
                            className={styles.exclamationIcon}
                            data-testid={TrailInfoDataTestId.Exclamation}
                        />
                        {getTrialBannerText()}
                    </View>
                    {getTrialBannerButton()}
                </Horizontal>
                <span className="calendar-id" />
            </View>
            <GlobalAlert.Toast {...toastAlert} />
        </>
    );
};
